import { useEffect, useRef, useState } from 'react';

/**
 * One-shot modal for the verification code emitted by
 * <c>POST /platform/accounts/*</c> (design D10 / spec "UX do modal de credencial
 * sensível"). Enforces the contract:
 *  - cannot close on backdrop click, Escape, or any X button — only via the
 *    explicit confirmation button whose label includes the target's name;
 *  - confirmation button is disabled for 3 s with a visible countdown;
 *  - focus is trapped inside the dialog while it is open;
 *  - the code value is never persisted to localStorage / sessionStorage and is
 *    removed from component state as soon as the user confirms (the parent
 *    receives the close callback and SHOULD null out its own copy too).
 */
export interface SensitiveCredentialModalProps {
  /** The plaintext verification code returned by the API (one-time). */
  verificationCode: string;
  /** Target operator's email for the contextual "convite enviado para …" line. */
  operatorEmail: string;
  /** Target operator's full name — required for the confirmation button label. */
  operatorFullName: string;
  /** Optional contextual headline shown above the email line. */
  contextLabel?: string;
  /** Called only when the operator clicks the explicit confirmation button. */
  onConfirm: () => void;
}

const COUNTDOWN_SECONDS = 3;

export function SensitiveCredentialModal({
  verificationCode,
  operatorEmail,
  operatorFullName,
  contextLabel,
  onConfirm,
}: SensitiveCredentialModalProps) {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [copied, setCopied] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const codeRef = useRef<HTMLDivElement | null>(null);

  // Countdown — 1 tick / second until the confirmation button is enabled.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  // Block Escape so this modal cannot be dismissed by accident.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  // Focus trap. Initial focus lands on the code block (it is tabbable via
  // tabIndex={0}) so the admin reads it first.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    codeRef.current?.focus();

    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onTab);
    return () => {
      document.removeEventListener('keydown', onTab);
      previouslyFocused?.focus?.();
    };
  }, []);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(verificationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard not allowed (older browsers / insecure context) — silently ignore.
    }
  }

  const codeSpaced = verificationCode.split('').join(' ');
  const codeAriaLabel =
    'código de verificação: ' + verificationCode.split('').join(', ');
  const buttonReady = secondsLeft <= 0;
  const buttonLabel = buttonReady
    ? `Já transmiti a ${operatorFullName}`
    : `Já transmiti (${secondsLeft}…)`;

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      // Backdrop click is intentionally a no-op — modal must stay open.
      onClick={(e) => e.stopPropagation()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sensitive-credential-modal-title"
        style={{
          background: 'var(--card-bg, #fff)',
          color: 'var(--text, #1C1917)',
          borderRadius: 12,
          padding: 32,
          maxWidth: 520,
          width: '90%',
          boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="sensitive-credential-modal-title"
          style={{ marginTop: 0, fontSize: 20, marginBottom: 8 }}
        >
          {contextLabel ?? 'Convite enviado'}
        </h2>
        <p style={{ marginBottom: 24, fontSize: 14, color: '#475569' }}>
          Convite enviado para <strong>{operatorEmail}</strong>. Link expira em 24 h.
        </p>

        <div
          ref={codeRef}
          tabIndex={0}
          aria-label={codeAriaLabel}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 40,
            letterSpacing: '0.25em',
            fontWeight: 700,
            textAlign: 'center',
            padding: '20px 16px',
            background: '#FFFBEB',
            border: '2px solid #F59E0B',
            borderRadius: 8,
            marginBottom: 8,
            outline: 'none',
          }}
        >
          {codeSpaced}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCopy}
            style={{ fontSize: 12 }}
            aria-label="Copiar código para a área de transferência"
          >
            {copied ? 'Copiado ✓' : 'Copiar'}
          </button>
        </div>

        <div
          role="alert"
          style={{
            background: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: 8,
            padding: 12,
            fontSize: 13,
            color: '#7F1D1D',
            marginBottom: 24,
          }}
        >
          <strong>Transmita esse código ao operador por canal seguro</strong>{' '}
          separado do email (Slack / verbal). <strong>NÃO</strong> inclua o link
          no canal do código. <strong>NÃO</strong> tire screenshot — copie e cole.
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn"
            disabled={!buttonReady}
            onClick={() => {
              if (!buttonReady) return;
              // We never persist the code; the parent must also null its copy.
              onConfirm();
            }}
            style={{
              background: buttonReady ? 'var(--primary, #F97316)' : '#CBD5E1',
              color: '#fff',
              padding: '10px 24px',
              borderRadius: 8,
              border: 'none',
              fontWeight: 600,
              cursor: buttonReady ? 'pointer' : 'not-allowed',
            }}
            aria-disabled={!buttonReady}
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
